import { database, DB_PREFIX, cacheGet, cacheSet, CACHE_TTL } from "./config";
import { ref, get, set, update, query, orderByChild, equalTo, push } from "firebase/database";
import { getHiringManagerProfile } from "./hiringmanager";
// ============================================================================
// ORGANIZATION & TEAM MANAGEMENT (Free Registration Expansion - DEV ONLY)
// ============================================================================

/**
 * Create a new organization
 * Called during hiring manager signup
 */
export const createOrganization = async (organizationData) => {
    if (!database) {
        console.error("Firebase not initialized");
        throw new Error("Firebase not initialized");
    }

    try {
        const orgsRef = ref(database, `${DB_PREFIX}organizations`);
        const newOrgRef = push(orgsRef);

        const now = Date.now();
        const billingPeriodStart = new Date();
        billingPeriodStart.setDate(1); // First of current month
        const billingPeriodEnd = new Date(billingPeriodStart);
        billingPeriodEnd.setMonth(billingPeriodEnd.getMonth() + 1);

        const dataToSave = {
            // Organization Info
            name: organizationData.companyName || organizationData.name,
            type: organizationData.type || 'individual', // 'individual' | 'business'
            plan: 'free',

            // Owner
            ownerUserId: organizationData.ownerUserId || null,
            ownerEmail: organizationData.ownerEmail,

            // Credits (organization-level for both types)
            creditBalance: 5, // Starting credits
            totalCreditsUsed: 0,

            // Seats (null = unlimited for free business)
            maxSeats: organizationData.type === 'business' ? null : 1,
            currentSeats: 1, // Owner counts as 1

            // Billing periods
            billingPeriodStart: billingPeriodStart.toISOString(),
            billingPeriodEnd: billingPeriodEnd.toISOString(),
            nextRenewalDate: billingPeriodEnd.toISOString(),

            // Timestamps
            createdAt: now,
            updatedAt: now
        };

        await set(newOrgRef, dataToSave);
        console.log("Organization created:", newOrgRef.key, "Type:", dataToSave.type);
        return { id: newOrgRef.key, ...dataToSave };
    } catch (error) {
        console.error("Error creating organization:", error);
        throw error;
    }
};

/**
 * Get organization by ID — cached
 */
export const getOrganization = async (organizationId) => {
    if (!database || !organizationId) return null;

    const cacheKey = `org:${organizationId}`;
    const cached = cacheGet(cacheKey);
    if (cached !== undefined) return cached;

    try {
        const orgRef = ref(database, `${DB_PREFIX}organizations/${organizationId}`);
        const snapshot = await get(orgRef);

        const result = snapshot.exists() ? { id: organizationId, ...snapshot.val() } : null;
        cacheSet(cacheKey, result, CACHE_TTL.organization);
        return result;
    } catch (error) {
        console.error("Error getting organization:", error);
        return null;
    }
};

/**
 * Get organization by owner email
 */

export const getOrganizationByOwnerEmail = async (email) => {
    if (!database || !email) return null;

    const cacheKey = `organization:owner:${email.toLowerCase()}`;
    const cached = cacheGet(cacheKey);
    if (cached !== undefined) return cached;

    try {
        const orgsRef = ref(database, `${DB_PREFIX}organizations`);
        const orgsQuery = query(
            orgsRef,
            orderByChild("ownerEmail"),
            equalTo(email)
        );

        const snapshot = await get(orgsQuery);

        let foundOrg = null;
        if (snapshot.exists()) {
            snapshot.forEach((childSnapshot) => {
                foundOrg = { id: childSnapshot.key, ...childSnapshot.val() };
            });
        }

        cacheSet(cacheKey, foundOrg, CACHE_TTL.organization);
        return foundOrg;
    } catch (error) {
        console.error("Error getting organization by owner email:", error);
        return null;
    }
};
/**
 * Get organization for a team member (by their email)
 */
export const getOrganizationForMember = async (memberEmail) => {
    if (!database || !memberEmail) return null;

    try {
        // First, get the user's profile to find their organizationId
        const profile = await getHiringManagerProfile(memberEmail);
        if (!profile?.organizationId) return null;

        return await getOrganization(profile.organizationId);
    } catch (error) {
        console.error("Error getting organization for member:", error);
        return null;
    }
};

/**
 * Update organization
 */
export const updateOrganization = async (organizationId, updates) => {
    if (!database || !organizationId) return false;

    try {
        const orgRef = ref(database, `${DB_PREFIX}organizations/${organizationId}`);
        await update(orgRef, {
            ...updates,
            updatedAt: Date.now()
        });
        return true;
    } catch (error) {
        console.error("Error updating organization:", error);
        return false;
    }
};
